/**
 * Invoice Header Component
 * Displays invoice number, dates, customer info, order reference, and status
 * AC-1: Invoice Header Section
 */

import { Card, CardContent } from '@/components/ui/card';
import { StatusBadge } from '@/components/common/StatusBadge';
import { InvoiceDetails } from '@/types';
import { format } from 'date-fns';
import { Calendar, User, FileText, MapPin, Phone, Mail } from 'lucide-react';

interface InvoiceHeaderProps {
  invoice: InvoiceDetails;
}

export const InvoiceHeader = ({ invoice }: InvoiceHeaderProps) => {
  const { customer, order } = invoice;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column: Invoice Details */}
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold mb-2">Invoice {invoice.invoiceNumber}</h2>
              <StatusBadge status={invoice.status} />
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Invoice Date:</span>
                <span className="font-medium">
                  {format(new Date(invoice.invoiceDate), 'dd MMM yyyy')}
                </span>
              </div>

              {invoice.dueDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Due Date:</span>
                  <span className="font-medium">
                    {format(new Date(invoice.dueDate), 'dd MMM yyyy')}
                  </span>
                </div>
              )}

              {order && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Order:</span>
                  <span className="font-medium">{order.orderNumber}</span>
                  <span className="text-muted-foreground text-xs">
                    ({format(new Date(order.orderDate), 'dd MMM yyyy')})
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Customer Information */}
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-semibold">Customer Details</h3>
              </div>
              <div className="space-y-1 text-sm pl-6">
                <p className="font-medium text-base">{customer.customerName}</p>

                {customer.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{customer.email}</span>
                  </div>
                )}

                {customer.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-3 w-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{customer.phone}</span>
                  </div>
                )}

                {customer.billingAddress && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-3 w-3 text-muted-foreground mt-0.5" />
                    <span className="text-muted-foreground">{customer.billingAddress}</span>
                  </div>
                )}

                {customer.gstin && (
                  <p className="text-muted-foreground">
                    <span className="font-medium">GSTIN:</span> {customer.gstin}
                  </p>
                )}

                {customer.pan && (
                  <p className="text-muted-foreground">
                    <span className="font-medium">PAN:</span> {customer.pan}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {invoice.notes && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">Notes:</span> {invoice.notes}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
